import { useState, useEffect } from "react";
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signInWithMicrosoft,
  sendPasswordResetEmail,
  getCurrentUser,
  getSession,
  onAuthStateChange
} from "./db/index.js";
import { getUserSubscription } from "./db/index.js";

export default function Login({ G, onLogin }) {
  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════
  // OAUTH REDIRECT DETECTION (for Google/Microsoft callback)
  // ═══════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    // Check if user just came back from OAuth redirect
    checkExistingSession();
    
    // Listen to auth state changes (OAuth completion, etc.)
    const { data: authListener } = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        await handleSuccessfulAuth();
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const checkExistingSession = async () => {
    try {
      const session = await getSession();
      if (session) {
        // User already signed in (OAuth redirect or existing session)
        await handleSuccessfulAuth();
      }
    } catch (err) {
      console.error("Session check error:", err);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // SUCCESSFUL AUTH HANDLER (common for all auth methods)
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleSuccessfulAuth = async () => {
    try {
      setLoading(true);
      setError("");
      
      // 1. Get current user
      const user = await getCurrentUser();
      
      if (!user) {
        setError("Failed to load user data. Please try again.");
        setLoading(false);
        return;
      }
      
      // 2. Get subscription/tier info
      let subscription;
      try {
        subscription = await getUserSubscription();
      } catch (subError) {
        console.error("Subscription fetch error:", subError);
        // Continue with default tier if subscription fetch fails
        subscription = {
  tierName: "Basic",
  tierLevel: 0,
  features: {},
  usage: {
    invoicesThisMonth: 0,
    invoiceLimit: 10,
    productsTotal: 0,
    productLimit: 5,
    customersTotal: 0,
    customerLimit: 5
  }
};
      }
      
      // 3. Call onLogin with enriched user object
onLogin({
  id: user.id,
  email: user.email,
  name: user.user_metadata?.full_name || 
        user.user_metadata?.name || 
        user.email.split('@')[0],
  tier: subscription.tierName,
  tierLevel: subscription.tierLevel,
  features: subscription.features || {},
  usage: subscription.usage || {
    invoicesThisMonth: 0,
    invoiceLimit: subscription.tierLevel === 0 ? 10 : 1000,
    productsTotal: 0,
    productLimit: subscription.tierLevel === 0 ? 5 : 500,
    customersTotal: 0,
    customerLimit: subscription.tierLevel === 0 ? 5 : 200
  }
});
      
    } catch (err) {
      console.error("Auth completion error:", err);
      setError("Failed to complete sign in. Please try again.");
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // SIGN IN (Email/Password)
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    
    setLoading(true);
    
    try {
      await signInWithEmail(email, password);
      // handleSuccessfulAuth will be called via onAuthStateChange
      
    } catch (err) {
      console.error("Sign in error:", err);
      
      if (err.message.includes("Invalid login credentials")) {
        setError("Invalid email or password");
      } else if (err.message.includes("Email not confirmed")) {
        setError("Please verify your email before signing in");
      } else {
        setError(err.message || "Failed to sign in. Please try again.");
      }
      
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // SIGN UP (Email/Password)
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    setLoading(true);
    
    try {
      const { user, session } = await signUpWithEmail(email, password);
      
      if (user && !session) {
        // Email verification required
        setMessage("Account created! Please check your email to verify your account.");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setMode("signin");
      } else if (session) {
        // Auto-signed in (email verification disabled)
        await handleSuccessfulAuth();
      }
      
      setLoading(false);
      
    } catch (err) {
      console.error("Sign up error:", err);
      
      if (err.message.includes("already registered")) {
        setError("This email is already registered");
      } else {
        setError(err.message || "Failed to create account. Please try again.");
      }
      
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // OAUTH (Google/Microsoft)
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    
    try {
      await signInWithGoogle();
      // Will redirect to Google - on return, checkExistingSession handles it
    } catch (err) {
      console.error("Google sign in error:", err);
      setError("Failed to sign in with Google. Please try again.");
      setLoading(false);
    }
  };
  
  const handleMicrosoftSignIn = async () => {
    setError("");
    setLoading(true);
    
    try {
      await signInWithMicrosoft();
      // Will redirect to Microsoft - on return, checkExistingSession handles it
    } catch (err) {
      console.error("Microsoft sign in error:", err);
      setError("Failed to sign in with Microsoft. Please try again.");
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // FORGOT PASSWORD
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    setLoading(true);
    
    try {
      await sendPasswordResetEmail(email);
      setMessage("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
      setLoading(false);
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err.message || "Failed to send reset email. Please try again.");
      setLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  
  return (
    <div style={{
      minHeight: "100vh",
      background: G.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Sora', sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>

      {/* Background Glows */}
      <div style={{
        position: "absolute", width: 480, height: 480, borderRadius: "50%",
        background: `radial-gradient(circle, ${G.accent}18, transparent 70%)`,
        top: -120, right: -120, pointerEvents: "none",
      }} />

      <div style={{
        position: "absolute", width: 360, height: 360, borderRadius: "50%",
        background: `radial-gradient(circle, ${G.accent}0e, transparent 70%)`,
        bottom: -80, left: -80, pointerEvents: "none",
      }} />

      {/* Main Card */}
      <div style={{
        background: G.surface,
        borderRadius: 14,
        padding: "40px 44px 44px 44px",
        width: "100%",
        maxWidth: 440,
        boxShadow: "0 32px 80px rgba(0,0,0,.4)",
        position: "relative",
        zIndex: 1,
        textAlign: "center",
        border: `1px solid ${G.border}`,
      }}>

        {/* App Icon */}
        <div style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 50, height: 50, background: G.accent, borderRadius: 13,
          marginBottom: 18, boxShadow: `0 4px 16px ${G.accent}44`,
        }}>
          <span style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>₹</span>
        </div>

        {/* Title */}
        <div style={{ 
          fontFamily: "'Lora', serif", 
          fontSize: 26, 
          fontWeight: 600, 
          color: G.ink, 
          marginBottom: 6 
        }}>
          Prime Invoices
        </div>
        
        <div style={{ 
          color: G.muted, 
          fontSize: 13, 
          marginBottom: 28, 
          lineHeight: 1.6 
        }}>
          Simple invoicing for freelancers<br />and small businesses
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TABS (Sign In / Sign Up) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        
        {!showForgotPassword && (
          <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 24,
            background: G.surface2,
            padding: 4,
            borderRadius: 8,
          }}>
            <button
              onClick={() => {
                setMode("signin");
                setError("");
                setMessage("");
              }}
              style={{
                flex: 1,
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                background: mode === "signin" ? G.surface : "transparent",
                color: mode === "signin" ? G.ink : G.muted,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                transition: "all .2s",
                boxShadow: mode === "signin" ? "0 1px 3px rgba(0,0,0,.1)" : "none",
              }}
            >
              Sign In
            </button>
            
            <button
              onClick={() => {
                setMode("signup");
                setError("");
                setMessage("");
              }}
              style={{
                flex: 1,
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                background: mode === "signup" ? G.surface : "transparent",
                color: mode === "signup" ? G.ink : G.muted,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                transition: "all .2s",
                boxShadow: mode === "signup" ? "0 1px 3px rgba(0,0,0,.1)" : "none",
              }}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FORGOT PASSWORD MODAL */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        
        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} style={{ textAlign: "left" }}>
            <div style={{ 
              fontSize: 18, 
              fontWeight: 600, 
              color: G.ink, 
              marginBottom: 8,
              textAlign: "center"
            }}>
              Reset Password
            </div>
            
            <div style={{ 
              fontSize: 13, 
              color: G.muted, 
              marginBottom: 20,
              textAlign: "center",
              lineHeight: 1.5
            }}>
              Enter your email address and we'll send you a link to reset your password.
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{
                fontSize: 11, fontWeight: 600, color: G.muted,
                textTransform: "uppercase", letterSpacing: .6,
                display: "block", marginBottom: 6
              }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 14,
                  outline: "none"
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "12px 20px",
                border: "none",
                borderRadius: 8,
                background: loading ? G.muted : G.accent,
                color: "#FFFFFF",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setError("");
              }}
              style={{
                width: "100%",
                padding: "10px 20px",
                border: `1px solid ${G.border}`,
                borderRadius: 8,
                background: G.surface2,
                color: G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Back to Sign In
            </button>
          </form>
        ) : (
          <>
            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* SIGN IN FORM */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            {mode === "signin" && (
              <form onSubmit={handleSignIn} style={{ textAlign: "left" }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                </div>

                <div style={{ 
                  textAlign: "right", 
                  marginBottom: 20 
                }}>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    style={{
                      background: "none",
                      border: "none",
                      color: G.accent,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: 8,
                    background: loading ? G.muted : G.accent,
                    color: "#FFFFFF",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: 20
                  }}
                >
                  {loading ? "Signing In..." : "Sign In"}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* SIGN UP FORM */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            {mode === "signup" && (
              <form onSubmit={handleSignUp} style={{ textAlign: "left" }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                  <div style={{ 
                    fontSize: 11, 
                    color: G.muted, 
                    marginTop: 4 
                  }}>
                    At least 6 characters
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    style={{
                      width: "100%",
                      padding: "11px 14px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.surface,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "12px 20px",
                    border: "none",
                    borderRadius: 8,
                    background: loading ? G.muted : G.accent,
                    color: "#FFFFFF",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    marginBottom: 20
                  }}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* ERROR/SUCCESS MESSAGES */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            {error && (
              <div style={{
                padding: "10px 14px",
                background: G.dangerBg,
                border: `1px solid ${G.danger}`,
                borderRadius: 7,
                color: G.danger,
                fontSize: 13,
                marginBottom: 20,
                textAlign: "left"
              }}>
                {error}
              </div>
            )}

            {message && (
              <div style={{
                padding: "10px 14px",
                background: "#F0FDF4",
                border: `1px solid ${G.success}`,
                borderRadius: 7,
                color: G.success,
                fontSize: 13,
                marginBottom: 20,
                textAlign: "left",
                lineHeight: 1.5
              }}>
                {message}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* DIVIDER */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 12, 
              margin: "24px 0" 
            }}>
              <div style={{ flex: 1, height: 1, background: G.border }} />
              <span style={{ 
                fontSize: 11, 
                color: G.muted, 
                letterSpacing: .5 
              }}>
                OR
              </span>
              <div style={{ flex: 1, height: 1, background: G.border }} />
            </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* OAUTH BUTTONS */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Google */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  padding: "11px 20px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 8,
                  background: G.surface,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  boxShadow: G.shadow,
                  transition: "border-color .2s, box-shadow .2s",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = "#4285F4";
                    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(66,133,244,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = G.border;
                    e.currentTarget.style.boxShadow = G.shadow;
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8H6.3C9.7 35.7 16.3 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.3 4.2-4.2 5.6l6.2 5.2C40.9 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-3.9z"/>
                </svg>
                Continue with Google
              </button>

              {/* Microsoft */}
              <button
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  padding: "11px 20px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 8,
                  background: G.surface,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  boxShadow: G.shadow,
                  transition: "border-color .2s, box-shadow .2s",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = "#00A4EF";
                    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0,164,239,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = G.border;
                    e.currentTarget.style.boxShadow = G.shadow;
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#FF5722" d="M6 6h18v18H6z"/>
                  <path fill="#4CAF50" d="M26 6h18v18H26z"/>
                  <path fill="#FFC107" d="M6 26h18v18H6z"/>
                  <path fill="#03A9F4" d="M26 26h18v18H26z"/>
                </svg>
                Continue with Microsoft
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}